import { v1, toResult } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { imagePatchBody } from '@/infrastructure/api/v1/mappers/images';
import { safeParseJsonObject } from '@/utils/safeParseJson';

/** A scan image's urls and metadata, as a v1 image carries them or as JSON text, as a legacy row does. */
interface ScanImageFields {
  originalUrl?: string | null;
  croppedUrl?: string | null;
  metadata?: unknown;
}

/** The metadata that marks a scan image as an anchor's thumbnail, so a later anchor may replace it. */
export function anchorThumbnailMetadata(url: string): Record<string, unknown> {
  return { anchorThumbnail: url };
}

/** Whether a scan image is an anchor's thumbnail rather than the creator's: uncropped, and its image is one of `anchorUrls` or the one it was seeded with. */
export function isAnchorThumbnail(image: ScanImageFields, anchorUrls: ReadonlySet<string>): boolean {
  if (image.croppedUrl || !image.originalUrl) return false;
  const seeded = safeParseJsonObject(image.metadata).anchorThumbnail;
  return seeded === image.originalUrl || anchorUrls.has(image.originalUrl);
}

/** Marks the scan images of the scene's story that show `anchorUrl` uncropped as that anchor's thumbnail, so once it is gone a new anchor's may replace them. */
export async function markAnchorThumbnails(sceneInstanceId: string, anchorUrl: string): Promise<void> {
  const { data: scene, error: sceneError } = await toResult(v1.GET('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: sceneInstanceId } } }));
  if (sceneError) throw sceneError;
  const storyId = scene.storyUniqueId;
  if (!storyId) return;
  const { data: scans, error } = await listAll(page => v1.GET('/api/v1/images', {
    params: { query: { sceneOriginUniqueId: [storyId], category: ['ScanImage'], ...page } },
  }));
  if (error) throw error;
  const unmarked = scans.filter((image) => !image.croppedUrl && image.originalUrl === anchorUrl && !isAnchorThumbnail(image, new Set()));
  for (const image of unmarked) {
    const metadata = { ...safeParseJsonObject(image.metadata), ...anchorThumbnailMetadata(anchorUrl) };
    const { error: markError } = await toResult(v1.PATCH('/api/v1/images/{uniqueId}', {
      params: { path: { uniqueId: image.uniqueId } },
      body: imagePatchBody({ image_metadata: JSON.stringify(metadata) }),
    }));
    if (markError) throw markError;
  }
}
